// src/data/a11yActivate.js
//
// PASS 2 of data\YCP_PLATFORM_REVIEW_PLAN_2026-10-25.md. 41 controls on this platform were a <div>
// or a <span> with an onClick: a mouse could use them and a keyboard could not. Where the element
// can simply BE a <button>, it is made one (that is always the better fix). Where it cannot - a
// table row, or a box whose children are block elements a button may not legally contain - this is
// the standard escape hatch, and it is one function instead of thirty hand-written copies:
//
//     <div {...activate(() => onOpen(id), 'Open claim ' + id)}> ... </div>
//
// It returns exactly what WCAG 4.1.2 and 2.1.1 require of a custom control and nothing more:
//   role="button"   so assistive tech announces it as a control, not as text
//   tabIndex={0}    so the keyboard reaches it in document order
//   onClick         the action
//   onKeyDown       Enter AND Space, with preventDefault on Space so the page does not scroll
//   aria-label      when the visible content is a number or a code that does not read as a name
//
// It deliberately does NOT swallow other keys, stop propagation, or add a title attribute: a
// tooltip is not a name (it is not read on a phone and not read by every screen reader), which is
// why the label is an aria-label here and visible text wherever the screen had room for it.
//
// Pure. Proved by scripts\test-a11y-activate.mjs.

const ACTIVATION_KEYS = ['Enter', ' ', 'Spacebar'];   // 'Spacebar' is old Edge/IE's name for the same key

export function activate(onAct, label) {
  const act = typeof onAct === 'function' ? onAct : null;
  const props = {
    role: 'button',
    tabIndex: 0,
    onClick: act || undefined,
    onKeyDown: act
      ? (e) => {
        if (!e || ACTIVATION_KEYS.indexOf(e.key) === -1) return;
        // Space scrolls the page by default on a non-button element; Enter does not, but calling
        // preventDefault for both keeps the two keys behaving identically, which is the point.
        if (typeof e.preventDefault === 'function') e.preventDefault();
        act(e);
      }
      : undefined,
  };
  const name = typeof label === 'string' ? label.trim() : '';
  if (name) props['aria-label'] = name;
  // A control with no action is not a control: it keeps the role off so nothing announces a dead box.
  if (!act) { delete props.role; delete props.tabIndex; }
  return props;
}

// True when a set of props would be operable by a keyboard. Used by the suite, and by anything that
// wants to assert the question rather than eyeball it.
export function isKeyboardOperable(props) {
  const p = props || {};
  return !!(p.role === 'button' && p.tabIndex === 0 && typeof p.onClick === 'function' && typeof p.onKeyDown === 'function');
}

export const ACTIVATION_KEY_NAMES = Object.freeze(ACTIVATION_KEYS.slice());
